import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '@api-new/authservice';
import { CompanyApiService } from '@api-new/companyservice';
import { UserApiService } from '@api-new/userservice';
import { ErrorReasonEnum } from '@error/ENUM_ErrorReason';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { ErrorModel } from '@shared-lib/models/error.model';
import { AnalyticsService } from '@shared/services/analytics.service';
import { NotificationsService } from '@shared/services/notifications.service';
import { brandingToSignInCompanyIdOrNetworkIdAdapter } from '@shared/store/auth/adapters/branding-to-sign-in-company-id-or-network-id.adapter';
import { SigninCompanyIdOrNetworkIdModel } from '@shared/store/auth/models/signin-company-id-or-network-id.model';
import { of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap, throttleTime, withLatestFrom } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { sendVerificationCodeRequestAdapter } from './adapters/send-verification-code-request.adapter';
import {
  authError,
  checkLoginMail,
  checkLoginMailFailure,
  checkLoginMailSuccess,
  checkManualLoginCode,
  getAuthPagesBranding,
  getAuthPagesBrandingFailure,
  getAuthPagesBrandingSuccess,
  getListOfSignInCompanies,
  getListOfSignInCompaniesFailure,
  getListOfSignInCompaniesSuccess,
  loginFailure,
  loginSuccess,
  loginWithToken,
  loginWithTokenSuccess,
  logoutAndRedirect,
  navigateToVerifyCode,
  navigateToVerifyCodeAndSendEmail,
  pendingSignInSuccess,
  selectSignInCompany,
  sendVerificationCode,
  sendVerificationCodeFailure,
  sendVerificationCodeSuccess,
  setPendingSignIn,
} from './auth.action';
import { selectAuthState, selectBranding, selectSignInCompanyId, selectSignInEmail } from './auth.selectors';

@Injectable()
export class AuthEffect {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly authApiService = inject(AuthApiService);
  private readonly userApiService = inject(UserApiService);
  private readonly companyApiService = inject(CompanyApiService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly analyticsService = inject(AnalyticsService);

  getListOfSignInCompanies = createEffect(() =>
    this.actions$.pipe(
      ofType(getListOfSignInCompanies),
      debounceTime(500),
      withLatestFrom(this.store.select(selectAuthState)),
      switchMap(([_, authState]) => {
        const signInCompanyIdOrNetworkId: SigninCompanyIdOrNetworkIdModel = brandingToSignInCompanyIdOrNetworkIdAdapter(
          authState.branding,
          authState.signInCompanyId,
        );
        return this.authApiService
          .HTTP_CP_ListSignInCompanies({
            email: authState.email,
            code: authState.lastSignInCode,
            ...signInCompanyIdOrNetworkId,
          })
          .pipe(
            map(({ companies }) => {
              return getListOfSignInCompaniesSuccess({
                companies: companies,
              });
            }),
            catchError((error: ErrorModel) => of(getListOfSignInCompaniesFailure({ error }))),
          );
      }),
    ),
  );

  handleListOfSignInCompaniesSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(getListOfSignInCompaniesSuccess),
      map(({ companies }) => {
        if (companies.length === 0) {
          return authError({ errorCode: ErrorReasonEnum.ClientSignInInvalidCode });
        }
        if (companies.length > 1) {
          void this.router.navigate(['/', paths.AUTH, paths.auth.ACCOUNT_SELECT]);
          return { type: 'NO_ACTION' };
        }
        return selectSignInCompany({ companyId: companies[0].id });
      }),
    ),
  );

  selectSignInCompany = createEffect(() =>
    this.actions$.pipe(
      ofType(selectSignInCompany),
      throttleTime(500),
      withLatestFrom(this.store.select(selectAuthState)),
      map(([{ companyId }, authState]) => {
        void this.router.navigate(['/', paths.AUTH, paths.auth.LOGIN_STATUS]);
        return setPendingSignIn({
          pendingSignIn: {
            email: authState.email,
            code: authState.lastSignInCode,
            profileCompanyId: companyId,
            signInCompanyId: authState.signInCompanyId,
          },
        });
      }),
    ),
  );

  storeSignInSession = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess, pendingSignInSuccess),
        tap(({ signInResponse }) => {
          this.analyticsService.setIsFirstSignIn(!!signInResponse.isFirstSignIn);
          this.authService.setAccessToken(signInResponse.accessToken, false);
        }),
      ),
    { dispatch: false },
  );

  loginSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        map(({ link }) => {
          if (link) {
            void this.router.navigateByUrl(link);
          } else {
            void this.router.navigate(['/', paths.PLATFORM]);
          }
        }),
      ),
    { dispatch: false },
  );

  loginWithTokenSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginWithTokenSuccess),
        map(({ signInResponse }) => {
          this.authService.setAccessToken(signInResponse.accessToken, true);
          void this.router.navigate(['/', paths.PLATFORM]);
        }),
      ),
    { dispatch: false },
  );

  loginWithToken = createEffect(() =>
    this.actions$.pipe(
      ofType(loginWithToken),
      map(({ token }) => {
        this.authService.setAccessToken(token, true);

        return loginWithTokenSuccess({
          signInResponse: { accessToken: token },
        });
      }),
      catchError((error) => of(loginFailure({ error }))),
    ),
  );

  logout = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logoutAndRedirect),
        switchMap(() =>
          this.authApiService.HTTP_CP_SignOut().pipe(
            tap(() => {
              this.notificationsService.unregisterToken();
              this.authService.logoutAndRedirect();
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  checkMailBeforeSignIn = createEffect(() =>
    this.actions$.pipe(
      ofType(checkLoginMail),
      withLatestFrom(this.store.select(selectBranding)),
      switchMap(([{ email }, branding]) =>
        this.userApiService
          .HTTP_CP_DoesEmailExist({
            email,
            ...(branding?.signInCompanyId && { companyId: branding.signInCompanyId }),
            ...(branding?.signInNetworkId && { networkId: branding.signInNetworkId }),
          })
          .pipe(
            map((response) => {
              localStorage.setItem('LAST_CHECKED_EMAIL', email);
              return checkLoginMailSuccess({ doesEmailExist: response.doesEmailExist || false });
            }),
            catchError((error) => of(checkLoginMailFailure({ error }))),
          ),
      ),
    ),
  );

  checkLoginMailSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(checkLoginMailSuccess),
        tap(({ doesEmailExist }) => {
          if (doesEmailExist) {
            this.store.dispatch(navigateToVerifyCodeAndSendEmail());
          }
        }),
      ),
    { dispatch: false },
  );

  navigateToVerifyCodeAndSendEmail = createEffect(
    () =>
      this.actions$.pipe(
        ofType(navigateToVerifyCodeAndSendEmail),
        withLatestFrom(this.store.select(selectSignInEmail)),
        map(([_, email]) => {
          void this.router.navigate([paths.AUTH, paths.auth.LOGIN_VERIFICATION]).then((didNavigate) => {
            if (didNavigate && !!email) {
              this.store.dispatch(sendVerificationCode({ email }));
            }
          });
        }),
      ),
    { dispatch: false },
  );

  navigateToVerifyCode = createEffect(
    () =>
      this.actions$.pipe(
        ofType(navigateToVerifyCode),
        map(() => {
          void this.router.navigate([paths.AUTH, paths.auth.LOGIN_VERIFICATION]);
        }),
      ),
    { dispatch: false },
  );

  sendVerificationCode = createEffect(() =>
    this.actions$.pipe(
      ofType(sendVerificationCode),
      withLatestFrom(this.store.select(selectBranding), this.store.select(selectSignInCompanyId)),
      switchMap(([{ email, isResend }, branding, signInCompanyId]) =>
        this.authApiService.HTTP_CP_ProvideClientUserSignInCode(sendVerificationCodeRequestAdapter(email, branding, signInCompanyId)).pipe(
          map(() => sendVerificationCodeSuccess({ isResend: !!isResend })),
          catchError((error) => of(sendVerificationCodeFailure({ error }))),
        ),
      ),
    ),
  );

  loginFailure = createEffect(() =>
    this.actions$.pipe(
      ofType(loginFailure, getListOfSignInCompaniesFailure),
      map(({ error }: { error: ErrorModel }) => {
        return authError({ errorCode: error.error.reason });
      }),
    ),
  );

  getAuthPagesBranding = createEffect(() =>
    this.actions$.pipe(
      ofType(getAuthPagesBranding),
      switchMap(({ domain }) =>
        this.companyApiService.HTTP_CP_LoadSignInBranding({ clientPortalDomain: domain }).pipe(
          map((branding) => getAuthPagesBrandingSuccess({ branding })),
          catchError((error) => of(getAuthPagesBrandingFailure({ error }))),
        ),
      ),
    ),
  );

  loginWithCodeManually = createEffect(() =>
    this.actions$.pipe(
      ofType(checkManualLoginCode),
      map(() => getListOfSignInCompanies()),
    ),
  );
}
